import {useTranslation} from "react-i18next";
import {Termination, TerminationField, TerminationStatus} from "../../../app/api/model/termination";
import {LabeledSection, LabeledValue} from "../../../components/Layout/Page";
import {TerminationItemsTable} from "./components/TerminationItemsTable";
import {getTerminationStatusChip} from "../helpers";
import {formatTimestamp} from "../../../utils/datetime";
import {Typography} from "@mui/material";
import {EMPTY_UI} from "../../../variables";
import EditIcon from "@mui/icons-material/Edit";
import {useModal} from "../../../hooks/useModal";
import UpdateDialog from "../../../components/Dialog/UpdateDialog";
import {useTerminationUpdateMutation} from "../../../app/api/api";
import {PermissionLevel} from "../../../app/api/model/user";

export interface TerminationGeneralTabProps {
    termination: Termination;
}

export function TerminationGeneralTab({termination}: Readonly<TerminationGeneralTabProps>) {
    const {t} = useTranslation();
    const updateCommentModal = useModal(false);
    const [updateTermination] = useTerminationUpdateMutation();

    const getLabeledValue = (labelKey: string, content: any) => (
        <LabeledValue label={t(labelKey)} content={content} key={labelKey} />
    );
    const isCommentEditable = termination[TerminationField.STATUS] !== TerminationStatus.DONE;
    const fields = [
        {
            label: "generic_id",
            content: termination[TerminationField.ID],
        },
        {
            label: "generic_customerNumber",
            content: termination[TerminationField.CUSTOMER_NUMBER],
        },
        {
            label: "generic_customerName",
            content: termination[TerminationField.CUSTOMER_NAME],
        },
        {
            label: "termination_terminationAt",
            content: formatTimestamp(termination[TerminationField.TERMINATION_AT]),
        },
        {
            label: "termination_isFullTermination",
            content: termination[TerminationField.IS_FULL_TERMINATION] ? t("generic_yes") : t("generic_no"),
        },
        {
            label: "generic_status",
            content: getTerminationStatusChip({t, status: termination[TerminationField.STATUS]}),
        },
        {
            label: "termination_statusChangedAt",
            content: formatTimestamp(termination[TerminationField.STATUS_CHANGED_AT]),
        },
        {
            label: "termination_adminMails",
            content: termination[TerminationField.ADMIN_MAILS],
        },
    ];

    const handleEditCommentSubmit = async (payload: Partial<Termination>) => {
        await updateTermination({id: termination[TerminationField.ID], termination: payload}).unwrap();
    };

    return (
        <>
            <LabeledSection title={t("generic_termination")}>
                {fields.map((f) => getLabeledValue(f.label, f.content))}
            </LabeledSection>
            <LabeledSection
                title={t("generic_comment")}
                xs={12}
                sx={{overflowWrap: "anywhere", maxWidth: "100%"}}
                buttons={
                    isCommentEditable
                        ? [
                              {
                                  key: "updateComment",
                                  tooltip: "action_updateComment",
                                  disabled: updateCommentModal.isOpen,
                                  minRequiredPermissionToSee: PermissionLevel.MANAGER,
                                  callback: () => updateCommentModal.setIsOpen(true),
                                  icon: <EditIcon />,
                              },
                          ]
                        : undefined
                }
            >
                <Typography sx={{paddingTop: "12px"}}>{termination[TerminationField.COMMENT] ?? EMPTY_UI}</Typography>
            </LabeledSection>
            <TerminationItemsTable termination={termination} />
            {isCommentEditable && (
                <UpdateDialog
                    titleKey="action_updateComment"
                    modal={updateCommentModal}
                    inputFields={[
                        {
                            translationKey: "generic_comment",
                            fieldName: TerminationField.COMMENT,
                            multiline: true,
                            minRows: 10,
                            gridProps: {xs: 12},
                        },
                    ]}
                    initialValues={{
                        [TerminationField.COMMENT]: termination[TerminationField.COMMENT],
                    }}
                    onSubmit={handleEditCommentSubmit}
                    onSuccess={updateCommentModal.onClose}
                />
            )}
        </>
    );
}
